import React, { forwardRef, Fragment, type ReactNode, type Ref } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fireScreenAnalyticsDeferred, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import type { FullscreenContainerProps } from '../../../types.tsx';
import ScrollContainer from './scroll-container/index.tsx';

export const FullscreenBody = ({ children, containerStyle }: FullscreenContainerProps) => (
	<ScrollContainer>
		<FullscreenBodyWrapper
			data-testid="minimizable-modal.ui.modal-container.fullscreen.fullscreen-body-wrapper"
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={containerStyle}
		>
			{children}
		</FullscreenBodyWrapper>
	</ScrollContainer>
);

export const Fullscreen = forwardRef<HTMLDivElement, FullscreenContainerProps>(
	({ children, className, components, containerStyle }: FullscreenContainerProps, ref) => {
		const { Header = Fragment, Footer = Fragment } = components || {};

		const { isTransitionInState } = useMinimizableModal();

		const isViewEntered = isTransitionInState(['fullscreen'], TransitionState.ENTERED);

		return (
			<FullscreenWrapper
				ref={ref}
				transitionClass={className}
				data-testid={!isViewEntered ? undefined : 'minimizable-modal.ui.modal-container.fullscreen'}
			>
				<MountEvent onMount={fireScreenAnalyticsDeferred} />
				{components ? (
					<>
						<Header />
						<FullscreenBody containerStyle={containerStyle}>{children}</FullscreenBody>
						<Footer />
					</>
				) : (
					<>{children}</>
				)}
			</FullscreenWrapper>
		);
	},
);

export const FullscreenWrapper = (props: {
	transitionClass?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: Ref<any>;
	'data-testid'?: string;
	children?: ReactNode;
}) => (
	<FullscreenWrapperExperimentStyles
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={props.transitionClass}
		{...props}
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullscreenBodyWrapper = styled.div({
	paddingTop: token('space.250'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullscreenWrapperExperimentStyles = styled.div({
	width: '100%',
	height: '100%',
	position: 'fixed',
	top: '0px',
	left: '0px',
	backgroundColor: token('elevation.surface.overlay'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});
